import type { RequestContext } from "../../../application/context/request-context.js";
import type { TechnicalDocumentReview, TechnicalDocumentType } from "../../../domain/review/technical-document-review.js";

const STORAGE_KEY = "technical_reviews";

export interface TechnicalReviewRepository {
  findById(context: RequestContext, id: string): Promise<TechnicalDocumentReview | null>;
  /** O ciclo mais recente do documento (qualquer status). */
  findLatestByDocument(context: RequestContext, documentType: TechnicalDocumentType, documentId: string): Promise<TechnicalDocumentReview | null>;
  listAll(context: RequestContext): Promise<readonly TechnicalDocumentReview[]>;
  save(context: RequestContext, review: TechnicalDocumentReview): Promise<void>;
}

/**
 * Marcador que substitui uma imagem no espelho em localStorage: 1x1
 * transparente, reconhecível e — importante — em **PNG**. O pdfmake só
 * desenha PNG e JPEG; um GIF aqui derruba a geração do PDF do PLH com
 * "Unknown image format" quando o snapshot reduzido é relido após um F5.
 */
export const STRIPPED_IMAGE_PLACEHOLDER =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

/** Acima disso um texto não é dado de formulário: é binário embutido. */
const INLINE_IMAGE_MIN_LENGTH = 512;

function isInlineImage(value: string): boolean {
  return value.length >= INLINE_IMAGE_MIN_LENGTH && /^data:[^;]*;base64,/i.test(value);
}

/**
 * Cópia do snapshot sem os binários embutidos. O snapshot documental carrega
 * fotos e croquis em base64: dois ou três relatórios já estouram a cota de
 * ~5 MB do localStorage, e aí a gravação do lote inteiro falhava — o usuário
 * perdia a revisão, não só a imagem.
 */
function withoutInlineImages<T>(value: T): T {
  if (typeof value === "string") return (isInlineImage(value) ? STRIPPED_IMAGE_PLACEHOLDER : value) as unknown as T;
  if (Array.isArray(value)) return value.map(item => withoutInlineImages(item)) as unknown as T;
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) out[key] = withoutInlineImages(item);
    return out as unknown as T;
  }
  return value;
}

/**
 * Espelhado em localStorage, como os demais repositórios do app real.
 *
 * A memória guarda a revisão COMPLETA (snapshot com as imagens) durante a
 * sessão. O espelho em localStorage guarda a mesma revisão sem os binários:
 * é o que cabe na cota e preserva todo o estado de workflow (status, ciclo,
 * PLH, datas, histórico) através de um F5. A versão íntegra e durável do
 * snapshot é responsabilidade da composição de backend (SQLite), que não tem
 * limite de cota.
 */
export class InMemoryTechnicalReviewRepository implements TechnicalReviewRepository {
  private reviews = new Map<string, TechnicalDocumentReview>();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      if (typeof localStorage !== "undefined") {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed: TechnicalDocumentReview[] = JSON.parse(stored);
          // O que já está em memória é mais completo que o espelho (que não
          // tem os binários): reler o storage não pode rebaixar a sessão.
          for (const r of parsed) if (!this.reviews.has(r.id)) this.reviews.set(r.id, r);
        }
      }
    } catch (e) {
      console.warn("Failed to load technical reviews from storage:", e);
    }
  }

  /**
   * Grava o espelho em degraus, do mais completo ao mínimo indispensável.
   * Nenhum degrau descarta a REVISÃO — só o peso do snapshot. Se nem o
   * mínimo couber, o erro sobe: aí é problema real, e falhar em silêncio
   * deixaria o usuário achando que enviou ao PLH sem ter enviado.
   */
  private persistStorage() {
    if (typeof localStorage === "undefined") return;
    const reviews = Array.from(this.reviews.values());

    const attempts: Array<{ label: string; build: () => TechnicalDocumentReview[] }> = [
      { label: "completo", build: () => reviews },
      { label: "sem imagens embutidas", build: () => reviews.map(r => withoutInlineImages(r)) },
      {
        label: "sem snapshots",
        build: () => reviews.map(r => ({ ...r, submissionSnapshot: null, approvalSnapshot: null } as unknown as TechnicalDocumentReview))
      }
    ];

    let lastError: unknown = null;
    for (const attempt of attempts) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(attempt.build()));
        if (attempt.label !== "completo") {
          console.warn(
            `[REVISÃO TÉCNICA] Cota do navegador excedida: o espelho local foi gravado ${attempt.label}. ` +
            "O estado do workflow está preservado; o snapshot íntegro depende do backend central."
          );
        }
        return;
      } catch (e) {
        lastError = e;
      }
    }

    console.error("Failed to persist technical reviews to storage:", lastError);
    throw lastError;
  }

  async findById(_context: RequestContext, id: string): Promise<TechnicalDocumentReview | null> {
    this.loadFromStorage();
    return this.reviews.get(id) || null;
  }

  async findLatestByDocument(_context: RequestContext, documentType: TechnicalDocumentType, documentId: string): Promise<TechnicalDocumentReview | null> {
    this.loadFromStorage();
    const list = Array.from(this.reviews.values())
      .filter(r => r.documentType === documentType && r.documentId === documentId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return list[0] || null;
  }

  async listAll(): Promise<readonly TechnicalDocumentReview[]> {
    this.loadFromStorage();
    return Array.from(this.reviews.values());
  }

  async save(_context: RequestContext, review: TechnicalDocumentReview): Promise<void> {
    this.reviews.set(review.id, { ...review });
    this.persistStorage();
  }
}
